import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { Plus, Edit, Briefcase } from 'lucide-react';
import { RecordActions } from '../../../components/common/RecordActions';
import { RecordViewDialog } from '../../../components/common/RecordViewDialog';
import { useAuth } from '../../../context/AuthContext';
import { hcmAPI } from '../../../services/api';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Card } from '../../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { toast } from 'sonner';

const WageTypes = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('wagetypes');
  const [viewItem, setViewItem] = useState(null);
  const navigate = useNavigate();
  
  const [wageTypes, setWageTypes] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formResetCounter, setFormResetCounter] = useState(0);
  
  // Form state
  const [selectedClass, setSelectedClass] = useState('EARNING');
  const [selectedTaxable, setSelectedTaxable] = useState('true');
  const [selectedRecurring, setSelectedRecurring] = useState('false');
  const [selectedFrequency, setSelectedFrequency] = useState('MONTHLY');
  const [selectedCurrency, setSelectedCurrency] = useState('INR');
  const [selectedStatus, setSelectedStatus] = useState('Active');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await hcmAPI.getWageTypes();
      setWageTypes(res.data);
    } catch (error) {
      console.error('Failed to load wage types:', error);
      toast.error('Failed to load wage types');
    }
  };

  const resetFormState = () => {
    setSelectedClass('EARNING');
    setSelectedTaxable('true');
    setSelectedRecurring('false');
    setSelectedFrequency('MONTHLY');
    setSelectedCurrency('INR');
    setSelectedStatus('Active');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    // Clean up empty strings
    Object.keys(data).forEach(key => {
      if (data[key] === '' || data[key] === null || data[key] === undefined) {
        if (['description', 'gl_account'].includes(key)) {
          delete data[key];
        }
      }
    });

    // Convert boolean and numeric fields
    if (data.is_taxable !== undefined) data.is_taxable = data.is_taxable === 'true';
    if (data.is_recurring !== undefined) data.is_recurring = data.is_recurring === 'true';
    if (data.default_amount) data.default_amount = parseFloat(data.default_amount);
    else delete data.default_amount;

    try {
      if (editingItem) {
        await hcmAPI.updateWageType(editingItem.wage_type_id, data);
        toast.success('Wage Type updated successfully');
      } else {
        await hcmAPI.createWageType(data);
        toast.success('Wage Type created successfully');
      }
      
      setIsDialogOpen(false);
      setEditingItem(null);
      resetFormState();
      setFormResetCounter(prev => prev + 1);
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const firstError = detail[0];
        const fieldName = firstError?.loc?.slice(-1)[0] || 'field';
        toast.error(`${fieldName}: ${firstError?.msg || 'Validation error'}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Operation failed');
      }
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setSelectedClass(item.wage_type_class || 'EARNING');
    setSelectedTaxable(item.is_taxable?.toString() || 'true');
    setSelectedRecurring(item.is_recurring?.toString() || 'false');
    setSelectedFrequency(item.frequency || 'MONTHLY');
    setSelectedCurrency(item.currency || 'INR');
    setSelectedStatus(item.status || 'Active');
    setIsDialogOpen(true);
    setFormResetCounter(prev => prev + 1);
  };

  const getClassBadgeColor = (wageClass) => {
    switch (wageClass) {
      case 'EARNING': return 'bg-green-100 text-green-800';
      case 'DEDUCTION': return 'bg-red-100 text-red-800';
      case 'ALLOWANCE': return 'bg-orange-100 text-orange-800';
      case 'BENEFIT': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Layout>
      <div className="p-6" data-testid="wage-types-page">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm')}>HCM</span>
            <span>/</span>
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm/config/wage-types')}>Configuration</span>
            <span>/</span>
            <span>Wage Types</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Wage Types Configuration</h1>
          <p className="text-gray-600 mt-1">Manage wage types for payroll processing (earnings, deductions, allowances, benefits)</p>
        </div>

        {/* Content Card */}
        <Card className="p-0 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
            <div className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-gray-500" />
              <h2 className="font-semibold text-gray-900">Wage Types</h2>
              <span className="text-sm text-gray-500">({wageTypes.length} total)</span>
            </div>
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) {
                setEditingItem(null);
                resetFormState();
              }
            }}>
              <DialogTrigger asChild>
                {writable && (<Button size="sm" data-testid="create-btn">
                  <Plus className="h-4 w-4 mr-1" />
                  Create Wage Type
                </Button>)}
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingItem ? 'Edit' : 'Create'} Wage Type</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} key={formResetCounter} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="wage_type_id">Wage Type ID *</Label>
                      <Input id="wage_type_id" name="wage_type_id" placeholder="e.g., 1001, HRA01" maxLength={10} defaultValue={editingItem?.wage_type_id || ''} disabled={!!editingItem} required />
                    </div>
                    <div>
                      <Label htmlFor="name">Name *</Label>
                      <Input id="name" name="name" placeholder="e.g., House Rent Allowance" defaultValue={editingItem?.name || ''} required />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="description">Description</Label>
                      <Input id="description" name="description" placeholder="Detailed description" defaultValue={editingItem?.description || ''} />
                    </div>
                    <div>
                      <Label htmlFor="wage_type_class">Class *</Label>
                      <Select value={selectedClass} onValueChange={setSelectedClass}>
                        <SelectTrigger><SelectValue placeholder="Select Class" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="EARNING">Earning</SelectItem>
                          <SelectItem value="DEDUCTION">Deduction</SelectItem>
                          <SelectItem value="ALLOWANCE">Allowance</SelectItem>
                          <SelectItem value="BENEFIT">Benefit</SelectItem>
                          <SelectItem value="OTHER">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      <input type="hidden" name="wage_type_class" value={selectedClass} />
                    </div>
                    <div>
                      <Label htmlFor="is_taxable">Is Taxable</Label>
                      <Select value={selectedTaxable} onValueChange={setSelectedTaxable}>
                        <SelectTrigger><SelectValue placeholder="Is Taxable?" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="true">Yes</SelectItem>
                          <SelectItem value="false">No</SelectItem>
                        </SelectContent>
                      </Select>
                      <input type="hidden" name="is_taxable" value={selectedTaxable} />
                    </div>
                    <div>
                      <Label htmlFor="is_recurring">Is Recurring</Label>
                      <Select value={selectedRecurring} onValueChange={setSelectedRecurring}>
                        <SelectTrigger><SelectValue placeholder="Is Recurring?" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="true">Yes (Monthly)</SelectItem>
                          <SelectItem value="false">No (One-time)</SelectItem>
                        </SelectContent>
                      </Select>
                      <input type="hidden" name="is_recurring" value={selectedRecurring} />
                    </div>
                    <div>
                      <Label htmlFor="frequency">Frequency</Label>
                      <Select value={selectedFrequency} onValueChange={setSelectedFrequency}>
                        <SelectTrigger><SelectValue placeholder="Select Frequency" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="MONTHLY">Monthly</SelectItem>
                          <SelectItem value="WEEKLY">Weekly</SelectItem>
                          <SelectItem value="DAILY">Daily</SelectItem>
                          <SelectItem value="ONE_TIME">One Time</SelectItem>
                          <SelectItem value="ANNUAL">Annual</SelectItem>
                        </SelectContent>
                      </Select>
                      <input type="hidden" name="frequency" value={selectedFrequency} />
                    </div>
                    <div>
                      <Label htmlFor="default_amount">Default Amount</Label>
                      <Input type="number" id="default_amount" name="default_amount" placeholder="0.00" step="0.01" defaultValue={editingItem?.default_amount || ''} />
                    </div>
                    <div>
                      <Label htmlFor="currency">Currency</Label>
                      <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
                        <SelectTrigger><SelectValue placeholder="Select Currency" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="INR">INR</SelectItem>
                          <SelectItem value="USD">USD</SelectItem>
                          <SelectItem value="EUR">EUR</SelectItem>
                        </SelectContent>
                      </Select>
                      <input type="hidden" name="currency" value={selectedCurrency} />
                    </div>
                    <div>
                      <Label htmlFor="gl_account">GL Account</Label>
                      <Input id="gl_account" name="gl_account" placeholder="e.g., 4000001" defaultValue={editingItem?.gl_account || ''} />
                    </div>
                    <div>
                      <Label htmlFor="status">Status</Label>
                      <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                        <SelectTrigger><SelectValue placeholder="Select Status" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Active">Active</SelectItem>
                          <SelectItem value="Blocked">Blocked</SelectItem>
                        </SelectContent>
                      </Select>
                      <input type="hidden" name="status" value={selectedStatus} />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" data-testid="submit-btn">{editingItem ? 'Update' : 'Create'}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">ID</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Name</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Class</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Taxable</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Recurring</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Default Amt</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {wageTypes.length > 0 ? (
                  wageTypes.map((item) => (
                    <tr key={item.wage_type_id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 font-mono text-sm font-semibold">{item.wage_type_id}</td>
                      <td className="py-3 px-4">
                        <div className="text-sm font-medium">{item.name}</div>
                        {item.description && <div className="text-xs text-gray-600">{item.description}</div>}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${getClassBadgeColor(item.wage_type_class)}`}>
                          {item.wage_type_class}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm">{item.is_taxable ? 'Yes' : 'No'}</td>
                      <td className="py-3 px-4 text-sm">{item.is_recurring ? 'Yes' : 'No'}</td>
                      <td className="py-3 px-4 text-sm">
                        {item.default_amount ? `${item.currency} ${item.default_amount.toLocaleString()}` : '-'}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${item.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <RecordActions doctype="wagetypes" onView={() => setViewItem(item)} onEdit={() => handleEdit(item)} />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-gray-500">
                      No wage types found. Click "Create Wage Type" to add a new entry.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default WageTypes;
